import { useState } from 'react';

import { useMount } from '@provider-portal/common/hooks/useMount';
import { Nullable } from '@provider-portal/common/types/types';

import { consoleLog, consoleWarn } from '../../utils/utils';

export interface DataFetcher<TData, TArgs extends unknown[] = []> {
  data: Nullable<TData>;
  error: Nullable<Error>;
  isFetching: boolean;
  fetch: (...args: TArgs) => Promise<Nullable<TData>>;
}

export interface DataFetcherHook<TData, TArgs extends unknown[] = []> {
  useFetcher: () => DataFetcher<TData, TArgs>;
}

interface CreateFetcherArgs<TEntity, TArgs extends unknown[]> {
  name?: string;
  fetch: (...args: TArgs) => Promise<TEntity>;
  catchError?: (error: unknown, ...args: TArgs) => Nullable<Error>;
}

export function createFetcher<TData, TArgs extends unknown[]>({
  name,
  fetch,
  catchError,
}: CreateFetcherArgs<TData, TArgs>): DataFetcherHook<TData, TArgs> {
  const internalState: {
    listeners: Set<(version: number) => void>;
    counter: number;
    fetchPromise: Nullable<Promise<Nullable<TData>>>;
  } = {
    listeners: new Set(),
    counter: 0,
    fetchPromise: null,
  };

  const log =
    name !== undefined
      ? (...args: any[]) => {
          consoleLog(`[FETCHER] ${name}`, ...args);
        }
      : () => null;

  const fire = () => {
    log(`fire on ${internalState.listeners.size} listeners`);
    internalState.listeners.forEach((listener) => listener(++internalState.counter));
  };
  const dataFetcher: DataFetcher<TData, TArgs> = {
    data: null,
    isFetching: false,
    error: null,
    fetch: (...args: TArgs) =>
      new Promise<Nullable<TData>>((resolve, reject) => {
        // We need setTimeout to hack React components onMount to settle
        setTimeout(async () => {
          log('start', JSON.stringify(args));
          if (dataFetcher.isFetching) {
            log('already fetching');
            if (internalState.fetchPromise) {
              await internalState.fetchPromise.then(resolve);
            } else {
              // Re-call parent method because it has already resolved until came this line
              await fetch(...args);
            }
            return;
          }
          // TODO to implement
          // internalState.fetchPromise = ...new Promise<Nullable<TData>>((r) => {
          log('needs fetch');
          dataFetcher.isFetching = true;
          fire();
          try {
            log('requesting...');
            const res = await fetch(...args);
            log('...got response');
            dataFetcher.data = res;
            dataFetcher.error = null;
          } catch (e) {
            if (catchError) {
              dataFetcher.error = catchError(e, ...args);
            } else if (e instanceof Error) {
              dataFetcher.error = e;
            } else {
              dataFetcher.error = new Error(`Something went wrong: ${JSON.stringify(e)}`);
            }
            dataFetcher.data = null;
          }

          dataFetcher.isFetching = false;
          fire();

          if (dataFetcher.error === null) {
            resolve(dataFetcher.data);
          } else {
            reject(dataFetcher.error);
          }
          log('end');
        });
      }),
  };

  return {
    useFetcher: (): DataFetcher<TData, TArgs> => {
      const [, setState] = useState(internalState.counter);

      useMount(() => {
        if (internalState.listeners.has(setState)) {
          consoleWarn('Should never happen');
        }
        internalState.listeners.add(setState);

        return () => {
          internalState.listeners.delete(setState);
        };
      });

      return dataFetcher;
    },
  };
}
